// Nhập ngân sách marketing đã duyệt theo tháng (tổng + phân bổ theo mạng quảng cáo).
import { useEffect, useState } from 'react';
import { DatePicker, Input, InputNumber, Modal, Popconfirm, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { BUDGET_NETWORKS, readBudgets, saveMonthBudget } from './budgetStore';
import { fmtMoney } from './insights';

const money = {
  formatter: (v?: string | number) => (v === undefined || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')),
  parser: (v?: string) => Number((v || '').replace(/\D/g, '')),
};

export function BudgetModal({ open, month: initial, onClose }: { open: boolean; month: Dayjs; onClose: () => void }) {
  const [month, setMonth] = useState(initial);
  const [total, setTotal] = useState<number | null>(null);
  const [networks, setNetworks] = useState<Record<string, number | null>>({});
  const [note, setNote] = useState('');
  const key = month.format('YYYY-MM');
  const exists = !!readBudgets()[key];

  // mở hộp hoặc đổi tháng → nạp số đã lưu của tháng đó
  useEffect(() => { if (open) setMonth(initial); }, [open, initial]);
  useEffect(() => {
    if (!open) return;
    const b = readBudgets()[key];
    setTotal(b?.total ?? null);
    setNetworks(b?.networks || {});
    setNote(b?.note || '');
  }, [open, key]);

  const netSum = Object.values(networks).reduce<number>((t, v) => t + (v || 0), 0);
  const finalTotal = total || netSum;
  const over = !!total && netSum > total;

  const save = () => {
    if (!finalTotal) { message.warning('Nhập tổng ngân sách hoặc ngân sách từng mạng'); return; }
    const clean = Object.fromEntries(Object.entries(networks).filter(([, v]) => v && v > 0)) as Record<string, number>;
    saveMonthBudget(key, { total: finalTotal, networks: clean, note: note.trim() || undefined });
    message.success(`Đã lưu ngân sách tháng ${month.format('MM/YYYY')}`);
    onClose();
  };
  const remove = () => {
    saveMonthBudget(key, null);
    message.success(`Đã xóa ngân sách tháng ${month.format('MM/YYYY')}`);
    onClose();
  };

  return (
    <Modal
      open={open} onCancel={onClose} onOk={save} okText="Lưu ngân sách" cancelText="Đóng" title="Ngân sách marketing đã duyệt" destroyOnClose
      okButtonProps={{ disabled: over }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex items-center gap-2">
          {exists && <Popconfirm title={`Xóa ngân sách tháng ${month.format('MM/YYYY')}?`} okText="Xóa" cancelText="Không" onConfirm={remove}><button type="button" className="mr-auto text-sm text-red-500 hover:underline">Xóa tháng này</button></Popconfirm>}
          <span className="ml-auto" /><CancelBtn /><OkBtn />
        </div>
      )}
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">Tháng</span>
          <DatePicker picker="month" format="MM/YYYY" allowClear={false} value={month} onChange={v => v && setMonth(v)} disabledDate={d => d.isAfter(dayjs().add(1, 'year'))} className="w-full" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">Tổng ngân sách đã duyệt (đ)</span>
          <InputNumber<number> min={0} step={1_000_000} value={total} onChange={setTotal} {...money} placeholder={netSum ? `${fmtMoney(netSum)} (tổng các mạng)` : 'VD: 120.000.000'} className="w-full" />
        </label>
        <div>
          <span className="mb-1 block text-xs text-slate-500">Phân bổ theo mạng (không bắt buộc)</span>
          <div className="grid grid-cols-2 gap-2">
            {BUDGET_NETWORKS.map(n => (
              <InputNumber<number>
                key={n} min={0} step={1_000_000} addonBefore={<span className="inline-block w-14 text-left text-xs">{n}</span>}
                value={networks[n] ?? null} onChange={v => setNetworks(s => ({ ...s, [n]: v }))} {...money} className="w-full"
              />
            ))}
          </div>
          {over && <div className="mt-1 text-xs text-red-500">Tổng các mạng ({fmtMoney(netSum)}) vượt tổng ngân sách ({fmtMoney(total!)})</div>}
        </div>
        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">Ghi chú</span>
          <Input.TextArea rows={2} maxLength={300} value={note} onChange={e => setNote(e.target.value)} placeholder="VD: Duyệt theo kế hoạch Q4, tăng TikTok cho dịp lễ" />
        </label>
        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Đang lưu trên trình duyệt này. Khi xem một phần tháng, ngân sách được chia theo số ngày (vd. 15/31 ngày → 15/31 ngân sách).
        </div>
      </div>
    </Modal>
  );
}
